import React, { useMemo, useState } from "react";
import { useReverification, useUser } from "@clerk/clerk-react";
import {
  isClerkAPIResponseError,
  isReverificationCancelledError,
} from "@clerk/clerk-react/errors";
import type { EmailAddressResource } from "@clerk/shared/types";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { toastError, toastWarning } from "../../../../utils/showApiErrorToast";
import {
  BROKER_ALERT_TITLE_CLASS,
  BROKER_FIELD_DESC_CLASS,
  BROKER_OUTLINE_BADGE_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import SettingCard, {
  accountSettingsFieldClassName,
  accountSettingsFieldRowClassName,
  accountSettingsFieldStackClassName,
  accountSettingsFieldTightControlClassName,
} from "./SettingCard";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function getClerkErrorMessage(error: unknown, fallback: string): string {
  if (isClerkAPIResponseError(error)) {
    return error.errors?.[0]?.longMessage || error.errors?.[0]?.message || fallback;
  }
  const err = error as { errors?: Array<{ longMessage?: string; message?: string }>; message?: string };
  return err?.errors?.[0]?.longMessage || err?.errors?.[0]?.message || err?.message || fallback;
}

function isEmailVerified(email: EmailAddressResource): boolean {
  return email.verification?.status === "verified";
}

const EmailSetting: React.FC = () => {
  const { user } = useUser();
  const [newEmail, setNewEmail] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<EmailAddressResource | null>(null);
  const [actingEmailId, setActingEmailId] = useState<string | null>(null);

  const createEmailAddress = useReverification((email: string) => {
    if (!user) {
      throw new Error("User not available");
    }
    return user.createEmailAddress({ email });
  });

  const setPrimaryEmail = useReverification((emailAddressId: string) => {
    if (!user) {
      throw new Error("User not available");
    }
    return user.update({ primaryEmailAddressId: emailAddressId });
  });

  const destroyEmailAddress = useReverification((emailAddressId: string) => {
    const email = user?.emailAddresses.find((item) => item.id === emailAddressId);
    if (!email) {
      throw new Error("Email address not found");
    }
    return email.destroy();
  });

  const primaryEmail = user?.primaryEmailAddress ?? user?.emailAddresses?.find(
    (email) => email.id === user.primaryEmailAddressId
  );
  const currentEmail = primaryEmail?.emailAddress || "No email";
  const showVerificationCode = Boolean(pendingEmail);

  const extraEmails = useMemo(() => {
    if (!user) return [];
    return user.emailAddresses.filter((email) => {
      if (email.id === user.primaryEmailAddressId) return false;
      if (pendingEmail && email.id === pendingEmail.id) return false;
      return true;
    });
  }, [pendingEmail, user]);

  const resetUpdateFlow = () => {
    setShowUpdateForm(false);
    setNewEmail("");
    setVerificationCode("");
    setPendingEmail(null);
  };

  const resolveEmailResource = (email: EmailAddressResource): EmailAddressResource => {
    return user?.emailAddresses.find((item) => item.id === email.id) ?? email;
  };

  const handleVerifyCode = async () => {
    if (!user || !pendingEmail) {
      toastError("Code required", { description: "Please enter the verification code." });
      return;
    }
    if (!verificationCode.trim()) {
      toastError("Code required", { description: "Please enter the verification code." });
      return;
    }

    const oldPrimaryId = user.primaryEmailAddressId;
    setIsVerifying(true);
    try {
      const created = resolveEmailResource(pendingEmail);
      const verified = await created.attemptVerification({ code: verificationCode.trim() });

      if (verified.verification?.status !== "verified") {
        toastError("Verification failed", {
          description: "Invalid verification code. Please try again.",
        });
        return;
      }

      await setPrimaryEmail(created.id);

      if (oldPrimaryId && oldPrimaryId !== created.id) {
        try {
          await destroyEmailAddress(oldPrimaryId);
        } catch (error: unknown) {
          if (!isReverificationCancelledError(error)) {
            toastWarning("Email update incomplete", {
              description:
                getClerkErrorMessage(
                  error,
                  "Your new email is now primary, but the previous address could not be removed. You can remove it below."
                ),
            });
          }
          await user.reload();
          resetUpdateFlow();
          return;
        }
      }

      toast.success("Email updated", {
        description: `Your primary email has been updated to ${created.emailAddress}.`,
      });
      await user.reload();
      resetUpdateFlow();
    } catch (error: unknown) {
      if (isReverificationCancelledError(error)) {
        return;
      }
      console.error("Error verifying code:", error);
      toastError("Verification failed", {
        description: getClerkErrorMessage(error, "Invalid verification code. Please try again."),
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleUpdateEmail = async () => {
    if (!user || !newEmail) return;

    const trimmed = newEmail.trim();
    if (!EMAIL_REGEX.test(trimmed)) {
      toastError("Invalid email", { description: "Please enter a valid email address." });
      return;
    }

    if (trimmed.toLowerCase() === currentEmail.toLowerCase()) {
      toast.info("Same email", { description: "This is already your current email address." });
      return;
    }

    setIsUpdating(true);
    try {
      let created: EmailAddressResource | undefined;
      try {
        created = await createEmailAddress(trimmed);
      } catch (error: unknown) {
        if (isReverificationCancelledError(error)) {
          return;
        }

        const existing = user.emailAddresses.find(
          (email) => email.emailAddress.toLowerCase() === trimmed.toLowerCase()
        );
        if (existing && isEmailVerified(existing)) {
          toast.info("Email already on your account", {
            description: "Set it as primary below, or remove the extra address.",
          });
          return;
        }
        if (existing) {
          created = existing;
        } else {
          throw error;
        }
      }

      if (!created) {
        throw new Error("User not available");
      }

      const createdEmail = created;
      await createdEmail.prepareVerification({ strategy: "email_code" });
      await user.reload();
      setPendingEmail(
        user.emailAddresses.find((email) => email.id === createdEmail.id) ?? createdEmail
      );
      setVerificationCode("");
      setShowUpdateForm(false);
      toast.success("Verification code sent", {
        description: `A verification code has been sent to ${createdEmail.emailAddress}. Enter it below to confirm the new address.`,
      });
    } catch (error: unknown) {
      if (isReverificationCancelledError(error)) {
        return;
      }
      console.error("Error updating email:", error);
      toastError("Update failed", {
        description: getClerkErrorMessage(error, "Failed to update email. Please try again."),
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSetPrimary = async (email: EmailAddressResource) => {
    if (!isEmailVerified(email)) {
      toastError("Verify first", {
        description: "Verify this email before making it your primary address.",
      });
      return;
    }

    setActingEmailId(email.id);
    try {
      await setPrimaryEmail(email.id);
      await user?.reload();
      toast.success("Email updated", {
        description: `Your primary email has been updated to ${email.emailAddress}.`,
      });
    } catch (error: unknown) {
      if (isReverificationCancelledError(error)) {
        return;
      }
      toastError("Update failed", {
        description: getClerkErrorMessage(error, "Could not set this email as primary."),
      });
    } finally {
      setActingEmailId(null);
    }
  };

  const handleRemoveEmail = async (email: EmailAddressResource) => {
    setActingEmailId(email.id);
    try {
      await destroyEmailAddress(email.id);
      await user?.reload();
      toast.success("Email removed", {
        description: `${email.emailAddress} has been removed from your account.`,
      });
    } catch (error: unknown) {
      if (isReverificationCancelledError(error)) {
        return;
      }
      toastError("Couldn't remove email", {
        description: getClerkErrorMessage(error, "Please try again."),
      });
    } finally {
      setActingEmailId(null);
    }
  };

  return (
    <SettingCard
      title="Email"
      description="Your email address. You can update it below. A verification code will be sent to the new address. Once verified, your email will be updated."
    >
      <div className={accountSettingsFieldStackClassName}>
        <Field className={accountSettingsFieldTightControlClassName}>
          <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Current email</FieldLabel>
          <div className={accountSettingsFieldRowClassName}>
            <Input
              id="current-email"
              value={currentEmail}
              readOnly
              className="min-w-0 flex-1 cursor-not-allowed bg-muted/50"
              data-font="primary"
            />
            {!showUpdateForm && !showVerificationCode ? (
              <Button
                type="button"
                variant="gradient"
                size="sm"
                data-font="primary"
                className="shrink-0"
                onClick={() => setShowUpdateForm(true)}
              >
                Update
              </Button>
            ) : null}
          </div>
        </Field>

        {extraEmails.length > 0 ? (
          <Field className={accountSettingsFieldClassName}>
            <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Additional emails</FieldLabel>
            <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
              These addresses are on your account but are not primary. Set one as primary or remove the extra.
            </FieldDescription>
            <div className={accountSettingsFieldStackClassName}>
              {extraEmails.map((email) => {
                const verified = isEmailVerified(email);
                const isActing = actingEmailId === email.id;
                return (
                  <div key={email.id} className={accountSettingsFieldRowClassName}>
                    <Input
                      value={email.emailAddress}
                      readOnly
                      className="min-w-0 flex-1 cursor-not-allowed bg-muted/50"
                      data-font="primary"
                    />
                    <Badge
                      variant="outline"
                      data-broker-outline-badge=""
                      className={BROKER_OUTLINE_BADGE_CLASS}
                    >
                      {verified ? "Verified" : "Unverified"}
                    </Badge>
                    <Button
                      type="button"
                      variant="gradient"
                      size="sm"
                      data-font="primary"
                      className="shrink-0"
                      disabled={isActing || !verified}
                      aria-busy={isActing || undefined}
                      onClick={() => void handleSetPrimary(email)}
                    >
                      {isActing ? (
                        <Loader2 data-icon="inline-start" className="size-3.5 animate-spin" aria-hidden />
                      ) : null}
                      Set as primary
                    </Button>
                    <Button
                      type="button"
                      variant="outline-red"
                      size="sm"
                      data-font="primary"
                      className="shrink-0"
                      disabled={isActing}
                      onClick={() => void handleRemoveEmail(email)}
                    >
                      Remove
                    </Button>
                  </div>
                );
              })}
            </div>
          </Field>
        ) : null}

        {showVerificationCode && pendingEmail ? (
          <Field className={accountSettingsFieldClassName}>
            <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Verification code</FieldLabel>
            <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
              Enter the verification code sent to {pendingEmail.emailAddress}
            </FieldDescription>
            <div className={accountSettingsFieldRowClassName}>
              <Input
                id="email-verification-code"
                type="text"
                placeholder="Enter verification code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                className="min-w-0 flex-1"
                maxLength={6}
                data-font="primary"
              />
              <Button
                type="button"
                variant="gradient"
                size="sm"
                data-font="primary"
                className="shrink-0"
                disabled={isVerifying}
                aria-busy={isVerifying || undefined}
                onClick={() => void handleVerifyCode()}
              >
                {isVerifying ? (
                  <Loader2 data-icon="inline-start" className="size-3.5 animate-spin" aria-hidden />
                ) : null}
                Verify
              </Button>
              <Button
                type="button"
                variant="outline-red"
                size="sm"
                data-font="primary"
                className="shrink-0"
                disabled={isVerifying}
                onClick={() => {
                  setPendingEmail(null);
                  setVerificationCode("");
                }}
              >
                Cancel
              </Button>
            </div>
          </Field>
        ) : null}

        {showUpdateForm && !showVerificationCode ? (
          <Field className={accountSettingsFieldTightControlClassName}>
            <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>New email address</FieldLabel>
            <div className={accountSettingsFieldRowClassName}>
              <Input
                id="new-email"
                type="email"
                placeholder="Enter new email address"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="min-w-0 flex-1"
                data-font="primary"
              />
              <Button
                type="button"
                variant="gradient"
                size="sm"
                data-font="primary"
                className="shrink-0"
                disabled={isUpdating}
                aria-busy={isUpdating || undefined}
                onClick={() => void handleUpdateEmail()}
              >
                {isUpdating ? (
                  <Loader2 data-icon="inline-start" className="size-3.5 animate-spin" aria-hidden />
                ) : null}
                Update
              </Button>
              <Button
                type="button"
                variant="outline-red"
                size="sm"
                data-font="primary"
                className="shrink-0"
                disabled={isUpdating}
                onClick={() => {
                  setShowUpdateForm(false);
                  setNewEmail("");
                }}
              >
                Cancel
              </Button>
            </div>
          </Field>
        ) : null}
      </div>
    </SettingCard>
  );
};

export default EmailSetting;
